import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HTTP_TIMEOUT_MS } from '../../../config'
import {
  Button,
  FormSection,
  Input,
  LoadingSpinner,
  SettingsSaveBar,
  Switch,
  useToastActions,
} from '@airclou/ui'
import { Info, RotateCcw } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { ErrorState } from '../../../components/state/ErrorState'
import { authServiceFetch } from '../../../services/authServiceFetch'

const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_SERVICE_URL || 'https://auth.airclou.com'

// ── Types ────────────────────────────────────────────────────────

interface PolicyData {
  tenantId: string
  loginMaxPerEmail: number | null
  loginMaxPerIp: number | null
  loginRateLimitWindowSecs: number | null
  lockoutThreshold: number | null
  lockoutDurationSecs: number | null
  effectivePolicy: EffectivePolicy
  globalDefaults: EffectivePolicy
}

interface EffectivePolicy {
  loginMaxPerEmail: number
  loginMaxPerIp: number
  loginRateLimitWindowSecs: number
  lockoutThreshold: number
  lockoutDurationSecs: number
}

type FieldKey = keyof Omit<PolicyData, 'tenantId' | 'effectivePolicy' | 'globalDefaults'>

// ── Field config ────────────────────────────────────────────────

interface FieldConfig {
  key: FieldKey
  labelKey: string
  descriptionKey: string
  unitKey: string
  /** 'lower' = lower is stricter (thresholds); 'higher' = higher is stricter (durations) */
  direction: 'lower' | 'higher'
  minimum: number
  /** Server-side ceiling (tenant-login-policy.ts MAXIMUMS). Must stay in sync. */
  maximum?: number
}

const FIELDS: FieldConfig[] = [
  {
    key: 'loginMaxPerEmail',
    labelKey: 'security.maxLoginPerEmail',
    descriptionKey: 'security.maxLoginPerEmailDesc',
    unitKey: 'security.attempts',
    direction: 'lower',
    minimum: 3,
  },
  {
    key: 'loginMaxPerIp',
    labelKey: 'security.maxLoginPerIp',
    descriptionKey: 'security.maxLoginPerIpDesc',
    unitKey: 'security.attempts',
    direction: 'lower',
    minimum: 1,
  },
  {
    key: 'loginRateLimitWindowSecs',
    labelKey: 'security.rateLimitWindow',
    descriptionKey: 'security.rateLimitWindowDesc',
    unitKey: 'security.seconds',
    direction: 'higher',
    minimum: 1,
    maximum: 24 * 60 * 60,
  },
  {
    key: 'lockoutThreshold',
    labelKey: 'security.lockoutThreshold',
    descriptionKey: 'security.lockoutThresholdDesc',
    unitKey: 'security.attempts',
    direction: 'lower',
    minimum: 3,
  },
  {
    key: 'lockoutDurationSecs',
    labelKey: 'security.lockoutDuration',
    descriptionKey: 'security.lockoutDurationDesc',
    unitKey: 'security.seconds',
    direction: 'higher',
    minimum: 60,
    maximum: 24 * 60 * 60,
  },
]

// ── Helpers ─────────────────────────────────────────────────────

function formatDuration(secs: number): string {
  if (secs >= 3600 && secs % 3600 === 0) return `${secs / 3600}h`
  if (secs >= 60 && secs % 60 === 0) return `${secs / 60}m`
  return `${secs}s`
}

function getBounds(
  field: FieldConfig,
  globalDefault: number,
): { min: number; max: number } {
  if (field.direction === 'lower') {
    // Lower is stricter — tenant can only set a value <= global default
    return { min: field.minimum, max: globalDefault }
  }
  // Higher is stricter — tenant can only set a value >= global default, up to
  // the server's abuse ceiling (a day). Advertising more than the server
  // accepts sends the operator into a 400 the input's own bounds said was fine.
  // If ops ever raises the env default past the ceiling, the default itself
  // stays reachable — mirroring the server's Math.max.
  return { min: globalDefault, max: Math.max(field.maximum ?? 999999, globalDefault) }
}

// ── Component ───────────────────────────────────────────────────

export function RateLimitsTab() {
  const { t } = useTranslation('dashboard')
  const { user, token } = useAuth()

  // Send the identity this page is displaying, not whoever holds the shared
  // .airclou.com session cookie. Same reasoning as the SSO page; see
  // apps/auth/docs/session-credential-standardization-plan.md (ID-3).
  const authHeaders = (extra?: Record<string, string>): Record<string, string> => ({
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  })
  const tenantId = user?.tenantId
  const { success: toastSuccess, error: toastError } = useToastActions()

  // Remote state
  const [policy, setPolicy] = useState<PolicyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form state: per-field overrides and "use default" toggles
  const [overrides, setOverrides] = useState<Record<FieldKey, string>>({
    loginMaxPerEmail: '',
    loginMaxPerIp: '',
    loginRateLimitWindowSecs: '',
    lockoutThreshold: '',
    lockoutDurationSecs: '',
  })
  const [useDefault, setUseDefault] = useState<Record<FieldKey, boolean>>({
    loginMaxPerEmail: true,
    loginMaxPerIp: true,
    loginRateLimitWindowSecs: true,
    lockoutThreshold: true,
    lockoutDurationSecs: true,
  })

  const [saving, setSaving] = useState(false)

  // ── Data fetching ─────────────────────────────────────────────

  // One place that turns a stored policy into form state: a null column means
  // "use the global default", anything else is this tenant's own value. Used on
  // load, after a save, and by Discard.
  const applyPolicyToForm = useCallback((data: PolicyData) => {
    const newDefaults: Record<FieldKey, boolean> = {} as any
    const newOverrides: Record<FieldKey, string> = {} as any
    for (const f of FIELDS) {
      const val = data[f.key]
      if (val === null) {
        newDefaults[f.key] = true
        newOverrides[f.key] = String(data.globalDefaults[f.key as keyof EffectivePolicy])
      } else {
        newDefaults[f.key] = false
        newOverrides[f.key] = String(val)
      }
    }
    setUseDefault(newDefaults)
    setOverrides(newOverrides)
  }, [])

  const fetchPolicy = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    setLoadError(null)
    try {
      const res = await authServiceFetch(
        `${AUTH_API_BASE}/api/auth/admin/tenant/${tenantId}/login-policy`,
        {
          headers: authHeaders(),
          credentials: 'include',
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
        },
      )
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Unknown error')

      const data: PolicyData = json.data
      setPolicy(data)

      // Populate form state from server data
      applyPolicyToForm(data)
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('security.failedToLoadPolicy')
      setLoadError(msg)
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    fetchPolicy()
  }, [fetchPolicy])

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!tenantId || !policy) return
    setSaving(true)

    // Build payload: null for "use default", integer for custom
    const body: Record<string, number | null> = {}
    for (const f of FIELDS) {
      if (useDefault[f.key]) {
        body[f.key] = null
      } else {
        const val = parseInt(overrides[f.key], 10)
        if (isNaN(val) || val <= 0) {
          toastError(t('security.mustBePositive', { label: t(f.labelKey) }))
          setSaving(false)
          return
        }
        body[f.key] = val
      }
    }

    try {
      const res = await authServiceFetch(
        `${AUTH_API_BASE}/api/auth/admin/tenant/${tenantId}/login-policy`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: JSON.stringify(body),
        },
      )
      const json = await res.json()
      if (!res.ok || !json.success) {
        const errorMsg =
          json.code === 'INVALID_POLICY_VALUE'
            ? json.details?.join('; ') || json.error
            : json.error || t('security.failedToLoadPolicy')
        toastError(errorMsg)
        setSaving(false)
        return
      }

      // Update local state from server response
      const data: PolicyData = json.data
      setPolicy(data)
      applyPolicyToForm(data)

      toastSuccess(t('security.policySaved'))
    } catch {
      toastError(t('security.networkError'))
    } finally {
      setSaving(false)
    }
  }

  // ── Reset all to defaults ────────────────────────────────────

  const handleResetAll = () => {
    if (!policy) return
    const newDefaults: Record<FieldKey, boolean> = {} as any
    const newOverrides: Record<FieldKey, string> = {} as any
    for (const f of FIELDS) {
      newDefaults[f.key] = true
      newOverrides[f.key] = String(policy.globalDefaults[f.key as keyof EffectivePolicy])
    }
    setUseDefault(newDefaults)
    setOverrides(newOverrides)
  }

  const handleDiscard = () => {
    if (policy) applyPolicyToForm(policy)
  }

  // ── Dirty check ──────────────────────────────────────────────

  const isDirty = (() => {
    if (!policy) return false
    for (const f of FIELDS) {
      const serverVal = policy[f.key]
      const isServerDefault = serverVal === null
      if (isServerDefault !== useDefault[f.key]) return true
      if (!useDefault[f.key]) {
        const currentVal = parseInt(overrides[f.key], 10)
        if (currentVal !== serverVal) return true
      }
    }
    return false
  })()

  // ── Render ────────────────────────────────────────────────────

  if (!tenantId) {
    return (
      <ErrorState
        title={t('security.noOrgSelected')}
        description={t('security.noOrgSelectedDesc')}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="text-sm text-blue-800 dark:text-blue-200">
          {t('security.rateLimitInfoBanner')}
        </div>
      </div>

      <FormSection
        title={t('security.loginRateLimits')}
        actions={
          policy && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
              disabled={saving}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('security.resetAllToDefaults')}
            </Button>
          )
        }
      >
        {loading ? (
          <div className="py-6">
            <LoadingSpinner />
          </div>
        ) : loadError ? (
          <ErrorState
            title={t('security.failedToLoadPolicy')}
            description={loadError}
            onRetry={fetchPolicy}
          />
        ) : policy ? (
          <div className="space-y-6">
            {FIELDS.map((field) => {
              const globalDefault =
                policy.globalDefaults[field.key as keyof EffectivePolicy]
              const isDefault = useDefault[field.key]
              const bounds = getBounds(field, globalDefault)
              const effectiveValue =
                policy.effectivePolicy[field.key as keyof EffectivePolicy]
              const unit = t(field.unitKey)

              return (
                <div
                  key={field.key}
                  className="rounded-lg border p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="font-medium text-foreground">
                        {t(field.labelKey)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t(field.descriptionKey)}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {t('security.systemDefault', { value: globalDefault, unit })}{' '}
                        {field.unitKey === 'security.seconds' &&
                          `(${formatDuration(globalDefault)})`}
                      </div>
                    </div>
                    <Switch
                      id={`default-${field.key}`}
                      checked={!isDefault}
                      onChange={() => {
                        setUseDefault((prev) => ({
                          ...prev,
                          [field.key]: !prev[field.key],
                        }))
                        // When switching to custom, prefill with the current effective value
                        if (isDefault) {
                          setOverrides((prev) => ({
                            ...prev,
                            [field.key]: String(effectiveValue),
                          }))
                        }
                      }}
                      label={t('security.custom')}
                      size="sm"
                      disabled={saving}
                    />
                  </div>

                  {!isDefault && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-40">
                        <Input
                          id={`input-${field.key}`}
                          type="number"
                          size="sm"
                          value={overrides[field.key]}
                          onChange={(val) =>
                            setOverrides((prev) => ({
                              ...prev,
                              [field.key]: val,
                            }))
                          }
                          disabled={saving}
                        />
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {unit}
                        {field.unitKey === 'security.seconds' &&
                          overrides[field.key] &&
                          !isNaN(parseInt(overrides[field.key], 10)) &&
                          parseInt(overrides[field.key], 10) > 0 &&
                          ` (${formatDuration(parseInt(overrides[field.key], 10))})`}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {field.direction === 'lower'
                          ? `min ${bounds.min}, max ${bounds.max}`
                          : `min ${bounds.min}`}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : null}
      </FormSection>

      <SettingsSaveBar
        visible={isDirty}
        onSave={handleSave}
        onDiscard={handleDiscard}
        isSaving={saving}
        message={t('common.unsavedChanges')}
        saveLabel={t('security.saveChangesBtn')}
        savingLabel={t('common.saving')}
        discardLabel={t('common.discard')}
      />
    </div>
  )
}
